<script lang="ts">
  import { createEventDispatcher, onMount } from "svelte";
  import { blur, fade } from "svelte/transition";

  import { loc } from "module/utils";
  import { loadSearchIndex, QuickInsert } from "module/core";
  import { stores } from "module/store/SettingsStore";
  import { ModuleSetting } from "module/store/ModuleSettings";
  import { ContextMode, type SearchContext } from "module/contexts";
  import type { SearchFilter } from "module/store/Filters";
  import KeyboardKey from "app/ui/KeyboardKey.svelte";

  import {
    SearchMode,
    type SearchControllerOptions,
    type SearchController,
    modeConfig,
    type SearchResultItem,
    type CallActionCallback,
  } from "./SearchControllers";
  import SearchInput from "./SearchInput.svelte";
  import SearchResults from "./SearchResults.svelte";

  const dispatch = createEventDispatcher();

  let {
    embedded = false,
    filterEditorOpen = false,
    context,
    filter,
    searchText = context?.startText || "",
    tooltips = embedded ? "OFF" : "LEFT",
  }: {
    embedded?: boolean;
    filterEditorOpen?: boolean;
    context?: SearchContext;
    filter?: SearchFilter;
    searchText?: string;
    tooltips?: "LEFT" | "RIGHT" | "OFF";
  } = $props();

  let mode: SearchMode = $state(SearchMode.DOCUMENT);

  let selectedAction: string | undefined = $state();
  let appElement: HTMLElement;

  let density = $derived(stores[ModuleSetting.SEARCH_DENSITY]);
  let showFootersetting = $derived(stores[ModuleSetting.SEARCH_FOOTER]);

  let selectedIndex = $state(-1);
  let results: SearchResultItem[] = $state.raw([]);

  let showFooter = $derived($showFootersetting && !embedded);

  $effect(() => {
    dispatch("setFilter", filter);
  });

  $effect(() => {
    dispatch("searched", searchText);
    selectedIndex = -1;
  });

  $effect(() => {
    if (selectedIndex === -1) {
      selectedIndex = results.length - 1;
    }
  });

  const callbacks: SearchControllerOptions = {
    getContext: () => context,
    close: () => dispatch("close"),
    setFilter: (newFilter: SearchFilter) => {
      filter = newFilter;
      inputChanged("");
      appElement.querySelector("input")?.focus();
    },
    refresh: () => {
      results = controller.search(searchText, filter);
    },
    setInputText: (inputText: string) => {
      inputChanged(inputText);
      appElement.querySelector("input")?.focus();
    },
  };

  let controller: SearchController = $state(
    modeConfig[SearchMode.DOCUMENT].controller(callbacks)
  );

  $effect.pre(() => {
    inputChanged(searchText);
  });

  let mainHint = $derived(
    controller.hint
      ? loc(controller.hint)
      : (filter?.subTitle ??
          (context?.mode === ContextMode.Insert
            ? loc("QUICKINSERT.ModeInsert")
            : "Quick Insert"))
  );

  // Focus and keep-open
  let mouseOver = false;
  let inputFocused = true;

  let element: HTMLElement;

  function getSelectedActionName() {
    const selectedId = selectedAction || results[selectedIndex]?.defaultAction;
    const action = results[selectedIndex]?.actions?.find(
      (a) => a.id === selectedId
    );
    if (action) {
      return loc(action.title);
    }
    return loc("QUICKINSERT.ActionSelect");
  }

  onMount(async () => {
    if (!QuickInsert.searchLib) {
      loadSearchIndex();
    }
  });

  function selectMode(nextMode: SearchMode) {
    if (mode === nextMode) return;
    controller = modeConfig[nextMode].controller(callbacks);
    mode = nextMode;
  }

  function inputChanged(textInput: string) {
    searchText = textInput;

    const availableModes =
      context?.mode === ContextMode.Insert
        ? Object.entries(modeConfig).filter(([, c]) => c.showInInsertMode)
        : Object.entries(modeConfig);

    const nextMode = filter
      ? SearchMode.DOCUMENT
      : (availableModes.find(
          ([, c]) => c.prefix && textInput.startsWith(c.prefix)
        )?.[0] as SearchMode | undefined) || SearchMode.DOCUMENT;

    selectMode(nextMode);
    results = controller.search(searchText, filter);
  }

  function onAction(
    actionId: string | undefined,
    item: unknown,
    shiftKey: boolean | undefined
  ) {
    controller.onAction(item, actionId, shiftKey);
  }

  const callAction: CallActionCallback = (action) => {
    const { actionId, item, shiftKey } = action;
    onAction(actionId, item, shiftKey);
  };
  function navigateUp() {
    selectedIndex = selectedIndex == 0 ? results.length - 1 : selectedIndex - 1;
    selectedAction = results[selectedIndex]?.defaultAction;
  }
  function navigateDown() {
    selectedIndex =
      selectedIndex === results.length - 1 ? 0 : selectedIndex + 1;
    selectedAction = results[selectedIndex]?.defaultAction;
  }

  function switchAction(event: CustomEvent) {
    if (!results[selectedIndex]) {
      return;
    }

    const actions = results[selectedIndex].actions;
    if (!actions || actions.length == 0) {
      controller.onTab(results[selectedIndex]);
      return;
    }
    let idx: number;
    if (selectedAction) {
      idx = actions.findIndex((a) => a.id == selectedAction);
    } else {
      idx = actions.findIndex(
        (a) => a.id == results[selectedIndex].defaultAction
      );
    }

    const delta = event.detail.shiftKey ? -1 : 1;
    const nextIdx = (idx + delta) % actions.length;
    selectedAction = actions[nextIdx >= 0 ? nextIdx : actions.length - 1].id;
  }

  function submitInput(event: CustomEvent) {
    if (!results[selectedIndex]) {
      return;
    }
    const { shiftKey } = event.detail;
    onAction(
      selectedAction || results[selectedIndex].defaultAction,
      results[selectedIndex].item,
      shiftKey
    );
  }
  function mouseEnter() {
    mouseOver = true;
  }
  function mouseLeave(evt: MouseEvent) {
    if (evt.shiftKey) return;
    mouseOver = false;
    checkFocus();
  }

  function inputFocus() {
    inputFocused = true;
  }
  function inputBlur() {
    inputFocused = false;
    checkFocus();
  }
  function checkFocus() {
    if (!inputFocused && !mouseOver) {
      dispatch("focusLost");
    }
  }

  function mouseDown(event: MouseEvent) {
    if (event.target !== element) {
      return;
    }

    dispatch("moveStart", event);
  }
</script>

<div
  class="quick-insert-app search-app density-{$density}"
  class:embedded
  class:application={!embedded}
  role="dialog"
  tabindex="0"
  on:mouseenter={mouseEnter}
  on:mouseleave={mouseLeave}
  bind:this={appElement}
  in:fade|global={{ duration: 100 }}
  out:blur|global={{ duration: 100 }}
>
  <SearchResults
    searchMode={mode}
    {tooltips}
    {selectedAction}
    {results}
    {selectedIndex}
    {callAction}
  ></SearchResults>
  <div class="drag-container" on:pointerdown={mouseDown} bind:this={element}>
    <div class="input-container">
      <SearchInput
        {filter}
        {filterEditorOpen}
        value={searchText}
        quickEditable={!embedded}
        showHelp={context?.mode !== ContextMode.Insert && !filter}
        on:inputChanged={(e) => inputChanged(e.detail)}
        on:navigateUp={navigateUp}
        on:navigateDown={navigateDown}
        on:switchAction={switchAction}
        on:submitInput={submitInput}
        on:clearFilter={() => !embedded && (filter = undefined)}
        on:focus={inputFocus}
        on:blur={inputBlur}
        on:close={() => dispatch("close")}
        on:openFilterEditor={() => dispatch("openFilterEditor", filter)}
      />
    </div>
    {#if showFooter}
      <div class="input-footer">
        <span class="hint">{mainHint}</span>
        {#if results.length}
          <span>
            {getSelectedActionName()}
            <KeyboardKey>↩</KeyboardKey>
          </span>
          {#if (results[selectedIndex]?.actions?.length || 0) > 1}
            <i class="separator"></i>
            <span>
              {loc("QUICKINSERT.ActionSwitch")}
              <KeyboardKey>↹</KeyboardKey>
            </span>
          {/if}
        {/if}
      </div>
    {/if}
  </div>
</div>

<style>
  .quick-insert-app {
    width: 100%;
    max-height: 100%;
    min-height: unset !important;
    overflow: hidden;
    display: flex;
    flex-direction: column;
    border: 1px solid var(--qiBorderColor);
    box-shadow: 0 0 20px #000;
    border-radius: 6px;
    backdrop-filter: blur(6px);
    pointer-events: auto;
    background: var(--qiBackground);
  }

  /* Dorako UI compatibility */
  :global([data-theme]).quick-insert-app,
  :global([data-theme].application) .quick-insert-app {
    background: var(--app-background);
    border: var(--app-border-width) solid var(--app-border-color);
  }
  .drag-container {
    cursor: move;
  }
  .input-container {
    pointer-events: none;
    padding: 0.4em 0.7em;
  }

  :global .quick-insert-app:has(input:focus) {
    border-color: var(--qiBorderColorFocused);
  }

  .input-footer {
    display: flex;
    height: 36px;
    gap: 0.5em;
    flex-shrink: 0;
    padding: 0.4em 0.7em;
    border-top: 1px solid var(--qiBorderColor);
    align-items: center;
    white-space: nowrap;
    pointer-events: none;
    line-height: 1.3em;
  }

  .hint {
    opacity: 0.5;
    flex-grow: 1;
    text-overflow: ellipsis;
    overflow: hidden;
  }

  .input-footer .separator {
    width: 2px;
    height: 0.9em;
    flex-shrink: 0;
    display: inline-block;
    background: var(--qiBorderColor);
    opacity: 0.6;
  }
</style>
